import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { fmtUSD, fmtDate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatusBadge, statusTone } from "../components/PageHeader";
import { DataTable } from "../components/DataTable";
import { Loader, KpiCard } from "../components/KpiCard";
import { Modal, Field, TextInput, SelectInput, AddButton } from "../components/Form";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "../components/ui/alert-dialog";
import { Users, DollarSign, Play, Loader2 } from "lucide-react";

export default function Payroll() {
  const { user } = useAuth();
  const canManage = ["admin", "finance_officer"].includes(user?.role);
  const [emps, setEmps] = useState(null);
  const [runs, setRuns] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);
  const [form, setForm] = useState({ name: "", title: "", department: "Programs", employment_type: "Full-Time", annual_salary: "", hire_date: "" });
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [freq, setFreq] = useState("Monthly");

  const load = () => { api.get("/employees").then((r) => setEmps(r.data)); api.get("/payroll/runs").then((r) => setRuns(r.data)); };
  useEffect(() => { load(); }, []);

  const addEmp = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/employees", { ...form, annual_salary: Number(form.annual_salary || 0) });
      toast.success("Employee added"); setOpen(false);
      setForm({ name: "", title: "", department: "Programs", employment_type: "Full-Time", annual_salary: "", hire_date: "" });
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const runPayroll = async () => {
    setRunning(true);
    try { await api.post("/payroll/runs", { period, frequency: freq }); toast.success("Payroll run processed"); load(); }
    catch (err) { toast.error(apiError(err)); } finally { setRunning(false); }
  };

  if (!emps) return <Loader />;
  const annual = emps.reduce((s, e) => s + (e.annual_salary || 0), 0);

  const empCols = [
    { key: "name", label: "Employee", className: "font-medium" },
    { key: "title", label: "Title", className: "text-[#5C5C54]" },
    { key: "department", label: "Department", className: "text-[#5C5C54]" },
    { key: "employment_type", label: "Type", render: (r) => <StatusBadge tone="blue">{r.employment_type}</StatusBadge> },
    { key: "annual_salary", label: "Annual Salary", align: "right", render: (r) => fmtUSD(r.annual_salary) },
    { key: "status", label: "Status", render: (r) => <StatusBadge tone={statusTone(r.status)}>{r.status}</StatusBadge> },
  ];
  const runCols = [
    { key: "period", label: "Period", className: "font-medium" },
    { key: "frequency", label: "Frequency", className: "text-[#5C5C54]" },
    { key: "run_date", label: "Run Date", render: (r) => fmtDate(r.run_date) },
    { key: "employee_count", label: "Employees", align: "right" },
    { key: "gross_total", label: "Gross Total", align: "right", render: (r) => <span className="font-semibold">{fmtUSD(r.gross_total)}</span> },
    { key: "status", label: "Status", render: (r) => <StatusBadge tone="green">{r.status}</StatusBadge> },
  ];

  return (
    <div>
      <PageHeader title="Payroll & Personnel" subtitle="Manage staff records and process payroll runs."
        action={canManage && <AddButton onClick={() => setOpen(true)} label="Add Employee" testid="add-emp-btn" />} />
      <div className="grid grid-cols-3 gap-6 mb-6">
        <KpiCard label="Headcount" value={emps.filter((e) => e.status === "Active").length} icon={Users} />
        <KpiCard label="Annual Payroll" value={fmtUSD(annual)} icon={DollarSign} accent="#178070" />
        <KpiCard label="Payroll Runs" value={runs.length} accent="#B45309" />
      </div>

      <Tabs defaultValue="personnel">
        <TabsList className="mb-5">
          <TabsTrigger value="personnel" data-testid="tab-personnel">Personnel</TabsTrigger>
          <TabsTrigger value="runs" data-testid="tab-runs">Payroll Runs</TabsTrigger>
        </TabsList>
        <TabsContent value="personnel">
          <DataTable columns={empCols} rows={emps} testid="employees-table" />
        </TabsContent>
        <TabsContent value="runs">
          {canManage && (
            <div className="bg-white border border-stone-200 rounded-xl p-5 mb-5 flex items-end gap-4 flex-wrap">
              <div><label className="block text-xs font-medium mb-1.5">Period</label>
                <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="border border-stone-300 rounded-md px-3 py-2 text-sm" /></div>
              <div><label className="block text-xs font-medium mb-1.5">Frequency</label>
                <select value={freq} onChange={(e) => setFreq(e.target.value)} className="border border-stone-300 rounded-md px-3 py-2 text-sm">
                  {["Monthly", "Semi-Monthly", "Bi-Weekly"].map((f) => <option key={f}>{f}</option>)}</select></div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button disabled={running} data-testid="run-payroll-btn"
                    className="bg-forest hover:bg-forest-900 text-white rounded-md px-4 py-2 text-sm font-medium flex items-center gap-2 disabled:opacity-60">
                    {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Run Payroll
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-white" data-testid="run-payroll-confirm">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="font-heading text-2xl">Run payroll for {period}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will process a {freq.toLowerCase()} payroll run for all {emps.filter((e) => e.status === "Active").length} active employees
                      (gross ≈ {fmtUSD(annual / (freq === "Monthly" ? 12 : freq === "Semi-Monthly" ? 24 : 26))}). Payroll runs are recorded permanently and cannot be deleted.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="run-payroll-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={runPayroll} data-testid="run-payroll-confirm-btn" className="bg-forest hover:bg-forest-900">Confirm & Run</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
          <DataTable columns={runCols} rows={runs} testid="runs-table" empty={{ title: "No payroll runs yet", hint: "Process your first run above." }} />
        </TabsContent>
      </Tabs>

      <Modal open={open} onOpenChange={setOpen} title="Add Employee" onSubmit={addEmp} busy={busy} testid="emp-modal">
        <Field label="Full Name" span={2}><TextInput required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
        <Field label="Title"><TextInput value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
        <Field label="Department"><SelectInput value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} options={["Programs", "Finance", "Operations", "Development", "Administration"]} /></Field>
        <Field label="Type"><SelectInput value={form.employment_type} onChange={(e) => setForm({ ...form, employment_type: e.target.value })} options={["Full-Time", "Part-Time", "Contract"]} /></Field>
        <Field label="Annual Salary"><TextInput type="number" value={form.annual_salary} onChange={(e) => setForm({ ...form, annual_salary: e.target.value })} /></Field>
        <Field label="Hire Date" span={2}><TextInput type="date" value={form.hire_date} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} /></Field>
      </Modal>
    </div>
  );
}
